const cloneDeep = require('lodash/cloneDeep')
const { parse } = require('../../../tools/smart-id')
const { suggestBatches } = require('../../../shipment/tools')
const { byId } = require('../../../tools/by-id')
const { getWithSiblingTotals } = require('../read/generic-utils')
const { distributeAvailableStock } = require('../distribute-stock')
const { applyPackoutToOrders, createAcceptedSnapshots } = require('./create-accepted-snapshots')

exports.acceptOrders = acceptOrders
function acceptOrders (
  {
    productsList,
    batchesList,
    user,
    snapshots: inputSnapshots,
    ledger,
    date,
    warehouseOrdersUseIssueUnits = false,
    noAcceptedSnapshotSplit = false,
    managedOrders = true
  }
) {
  // Self-ordering tenants place one order per group destined to the facility itself,
  // so multiple pending groups legitimately share a destinationId
  const snapshots = managedOrders
    ? squashOrdersByDestinationId(inputSnapshots)
    : inputSnapshots
  const productsById = byId(productsList)

  // Some tenant warehouses use packs/issue units instead of reporting units, normalize all
  // order quantities to reporting units before any stock math runs.
  // This must happen before distributeAvailableStock so that order
  // quantities and ledger values are in the same unit system.
  const normalizedSnapshots = warehouseOrdersUseIssueUnits
    ? convertSnapshotsToReportingUnits(snapshots, productsById)
    : snapshots

  const ledgerForPackOut = getLedgerForPackOut(ledger)
  // distributeAvailableStock doesn't know about sibling products, so here we have to
  // get all products to have the same totals as their siblings, so distributeAvailableStock
  // knows about it.
  // (NB this leans on the fact that QTOs will not live across sibling products in PSM)
  const ledgerForDistribute = getWithSiblingTotals({stock: ledgerForPackOut, productsById})
  const snapshotsWithAvailableQuantities = distributeAvailableStock(normalizedSnapshots, ledgerForDistribute)
  const snapshotsWithPackOut = applyPackoutToOrders(
    snapshotsWithAvailableQuantities,
    ledgerForPackOut,
    productsList,
    managedOrders
  )
  const batchesById = byId(batchesList)
  const acceptedSnapshots = createAcceptedSnapshots({user, snapshots: snapshotsWithPackOut, productsById, noAcceptedSnapshotSplit})
    .filter(snapshot => Object.keys(snapshot.products).length)

  return applyBatchesToSnapshots(
    {ledger, snapshots: acceptedSnapshots, productsById, batchesById, date}
  )
}

exports.applyBatchesToSnapshots = applyBatchesToSnapshots
function applyBatchesToSnapshots (
  {ledger: inputLedger, snapshots, productsById, batchesById, date}
) {
  // Clone ledger up front so we can mutate freely across the whole map without
  // touching the caller's ledger.
  const ledger = cloneDeep(inputLedger)
  const batchErrors = new Set()

  const result = snapshots.map(orderSnapshot => {
    const orderProducts = {}
    Object.keys(orderSnapshot.products).forEach(productId => {
      let {original} = orderSnapshot.products[productId]
      original = toPositiveIntegerOrZero(original)
      orderProducts[productId] = {original, adjusted: original}
      const shoppingList = {[productId]: original}
      const fefoBatches = suggestBatches(
        {ledger, batches: batchesById, shoppingList, products: productsById, expiresAfter: date}
      )

      const tbdBatch = Object.keys(fefoBatches)
        .find(batchId => batchId.includes(':manufacturer:tbd:batchNo:TBD'))
      if (tbdBatch) {
        batchErrors.add(`Stock not sufficient for '${productId}'`)
        return
      }

      const missingFromMasterData = Object.keys(fefoBatches).filter(batchId => !batchesById[batchId])
      if (missingFromMasterData.length) {
        missingFromMasterData.forEach(batchId => {
          batchErrors.add(`No batch master data found for '${batchId}'`)
        })
        return
      }

      orderProducts[productId].batches = fefoBatchesToOrderShape(fefoBatches)
      subtractBatchesFromLedgerInPlace(ledger, fefoBatches)
    })
    return Object.assign({}, orderSnapshot, {
      products: orderProducts
    })
  })

  if (batchErrors.size > 0) {
    const error = new Error('Cannot accept order, batch validation failed')
    error.status = 400
    error.errors = [...batchErrors]
    throw error
  }

  return result
}

// NB 1: if this is needed elsewhere, move it to a ledger tools directory.
// (has some similarity to tools.getLedgerBalance shipment counts to batches but too confusing to combine)
// NB 2: this doesn't handle commits, if you need to use balances/getCommitsTotal
exports.subtractBatchesFromLedger = subtractBatchesFromLedger
function subtractBatchesFromLedger (inputLedger, batches) {
  const ledger = cloneDeep(inputLedger)
  subtractBatchesFromLedgerInPlace(ledger, batches)
  return ledger
}

function subtractBatchesFromLedgerInPlace (ledger, batches) {
  for (let batchId in batches) {
    const { product } = parse(batchId)
    const productId = `product:${product}`

    const {quantity: batchQuantity} = batches[batchId]

    ledger[productId] = ledger[productId] || {total: 0, batches: {}}
    ledger[productId].batches[batchId] = ledger[productId].batches[batchId] || 0
    ledger[productId].batches[batchId] -= batchQuantity
    if (ledger[productId].batches[batchId] === 0) {
      delete ledger[productId].batches[batchId]
    }
    ledger[productId].total -= batchQuantity
    ledger[productId].availableTotal -= batchQuantity
    if (ledger[productId].total === 0) {
      delete ledger[productId]
    }
  }
  return ledger
}

function fefoBatchesToOrderShape (batches) {
  return Object.keys(batches)
    .reduce((acc, batchId) => {
      acc[batchId] = {original: batches[batchId].quantity, adjusted: batches[batchId].quantity}
      return acc
    }, {})
}

function getLedgerForPackOut (ledger) {
  return Object.keys(ledger)
    .reduce((acc, productId) => {
      acc[productId] = ledger[productId].availableTotal
      return acc
    }, {})
}

function toPositiveIntegerOrZero (quantity) {
  return Math.floor(
    Math.max(quantity, 0)
  )
}

exports.convertSnapshotsToReportingUnits = convertSnapshotsToReportingUnits
function convertSnapshotsToReportingUnits (snapshots, productsById) {
  return snapshots.map(snapshot => {
    const products = Object.keys(snapshot.products).reduce((acc, productId) => {
      const product = productsById[productId]
      if (!product) {
        console.warn(`convertSnapshotsToReportingUnits: product ${productId} not found in catalog, skipping conversion`)
        acc[productId] = snapshot.products[productId]
        return acc
      }
      const genericFactor = product.genericFactor || (product.unitOfIssue || 1) / (product.unitOfReporting || 1)
      const p = snapshot.products[productId]
      // Only convert original and adjusted
      // as these are in issue units. soh comes from the facility's stock report and
      // is already in reporting units.
      acc[productId] = Object.assign({}, p, {
        original: p.original * genericFactor,
        adjusted: p.adjusted * genericFactor
      })
      return acc
    }, {})
    return Object.assign({}, snapshot, { products })
  })
}

exports.squashOrdersByDestinationId = squashOrdersByDestinationId
function squashOrdersByDestinationId (snapshots) {
  const snapshotsSorted = createdFirst(snapshots)
  const snapshotsMergedBydestinationId = snapshotsSorted
    .reduce((acc, snapshot) => {
      if (!acc[snapshot.destinationId]) {
        acc[snapshot.destinationId] = cloneDeep(snapshot)
        return acc
      }

      Object.keys(snapshot.products).forEach(productId => {
        if (!acc[snapshot.destinationId].products[productId]) {
          acc[snapshot.destinationId].products[productId] = snapshot.products[productId]
          return
        }
        acc[snapshot.destinationId].products[productId].original += snapshot.products[productId].original
        acc[snapshot.destinationId].products[productId].adjusted += snapshot.products[productId].adjusted

        // in the chance that a bulk upload order came first, which do not have soh nor consumed,
        // take those numbers from the later snapshot
        if (snapshot.products[productId].soh !== undefined && snapshot.products[productId].soh !== undefined) {
          acc[snapshot.destinationId].products[productId].soh = snapshot.products[productId].soh
          acc[snapshot.destinationId].products[productId].consumed = snapshot.products[productId].consumed
        }
      })
      return acc
    }, {})

  return Object.values(snapshotsMergedBydestinationId)
}

function createdFirst (snapshots) {
  return snapshots.sort((a, b) => {
    return (a.createdAt > b.createdAt) ? 1 : ((a.createdAt < b.createdAt) ? -1 : 0)
  })
}
